import { Alert, Button, Card, Space, Spin, Steps, Tag } from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  KeyOutlined,
  MailOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get } from '../../api/client';
import { NamespaceConfigForm } from './components/NamespaceConfigForm';
import type { NamespaceConfig } from './components/NamespaceConfigForm';

type ConfigValues = Record<string, string | number | boolean>;
type ConfigStatus = { color: 'success' | 'warning' | 'error' | 'default'; text: string };

function useNamespaceConfig(name: string) {
  return useQuery({
    queryKey: ['system-config', name],
    queryFn: async () => (await get<unknown>(`/admin/system-config/${name}`)) as NamespaceConfig,
  });
}

function useConfigStatus(name: string, compute: (values: ConfigValues) => ConfigStatus): ConfigStatus | null {
  const { data } = useNamespaceConfig(name);
  if (!data) return null;
  return compute(data.values ?? {});
}

// 短信与实名都只剩阿里云一套凭据键：腾讯云两条通道后端均未实现（短信是 TODO、实名是伪实现），
// provider 选项与密钥字段已从 backend namespaces 移除，这里再列就会在「去补凭据」里出现无处保存的空字段。
const SMS_CREDENTIAL_KEYS = ['aliyunAccessKeyId', 'aliyunAccessKeySecret'];
const IDENTITY_CREDENTIAL_KEYS = [
  'accessKeyId',
  'accessKeySecret',
  'apiEndpoint',
  'appId',
  'appKey',
];

const STEP_HINTS = [
  '配置注册条款内容与强制阅读倒计时。用户注册第一步必须阅读条款到底部并勾选同意才能继续（App / Web 共用）。',
  '信息填写环节的验证手段。邮箱验证码依赖的 SMTP 连接参数已收进「服务凭据 → 邮件 SMTP」，此处只查看配置状态；提交动作由服务端限流兜底机器人。',
  '配置实名核验（姓名 + 身份证 + 手机号三要素）。调试用 test 服务商免计费，上线前切换真实渠道并关闭「模拟验证不通过」。',
  '配置手机号持有验证。向手机号下发短信验证码确认机主本人，用于老号回收、非本人申诉、升格换号等场景。',
];

function StepHeader({ status, onCredentials }: { status: ConfigStatus[]; onCredentials?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <Space wrap>
        {status.map((s, i) => (
          <Tag key={i} color={s.color}>
            {s.text}
          </Tag>
        ))}
        {onCredentials && (
          <Button size="small" icon={<KeyOutlined />} onClick={onCredentials}>
            配置凭据
          </Button>
        )}
      </Space>
    </div>
  );
}

function TermsForm() {
  const { data, isLoading } = useNamespaceConfig('terms');
  if (isLoading || !data) return <Spin />;
  return <NamespaceConfigForm config={data} />;
}

function SmsForm() {
  const { data, isLoading } = useNamespaceConfig('sms');
  if (isLoading || !data) return <Spin />;
  return (
    <NamespaceConfigForm
      config={data}
      excludeKeys={SMS_CREDENTIAL_KEYS}
      switchCardKeys={['notifyEnabled']}
      labelOverrides={{
        notifyEnabled: {
          label: '启用短信通知',
          desc: '开启后人工审核通过/驳回等结果以短信通知申请人；关闭则仅站内通知与邮件',
        },
      }}
    />
  );
}

function IdentityForm() {
  const { data, isLoading } = useNamespaceConfig('identity');
  if (isLoading || !data) return <Spin />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <NamespaceConfigForm
        config={data}
        excludeKeys={IDENTITY_CREDENTIAL_KEYS}
        switchCardKeys={['enabled', 'fallbackToBackupPhone', 'mockVerifyFail']}
        labelOverrides={{
          enabled: {
            label: '启用实名认证',
            desc: '开启后注册流程需通过姓名+身份证+手机号三要素核验才能继续；关闭则跳过该步',
          },
          fallbackToBackupPhone: {
            label: '备用电话回退',
            desc: '主手机验证失败时自动用备用电话重试一次（仍按服务商计费）',
          },
          mockVerifyFail: {
            label: '模拟验证不通过',
            desc: '测试用：开启后三要素验证一律失败且不调用第三方；上线前务必关闭',
          },
        }}
      />
    </div>
  );
}

export default function RegisterFlowPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const goCredentials = () => navigate('/settings/credentials');

  const termsStatus = useConfigStatus('terms', (v) =>
    String(v.content ?? '').trim()
      ? { color: 'success', text: '条款已配置' }
      : { color: 'warning', text: '条款未配置' },
  );
  const smtpStatus = useConfigStatus('smtp', (v) =>
    v.host && v.user
      ? { color: 'success', text: 'SMTP 已配置' }
      : { color: 'warning', text: 'SMTP 未配置' },
  );
  const identityStatus = useConfigStatus('identity', (v) => {
    if (!v.enabled) return { color: 'default', text: '未启用' };
    if (v.mockVerifyFail) return { color: 'error', text: '模拟失败已开启' };
    if (v.provider === 'test') return { color: 'warning', text: '调试模式 test' };
    return { color: 'success', text: `已启用 · ${v.provider}` };
  });
  const smsStatus = useConfigStatus('sms', (v) =>
    v.provider === 'mock'
      ? { color: 'warning', text: '调试模式 mock' }
      : { color: 'success', text: `生产 · ${v.provider}` },
  );

  const steps = [
    {
      key: 'terms',
      icon: <FileTextOutlined />,
      title: '条款阅读',
      status: termsStatus ? [termsStatus] : [],
      panel: <TermsForm />,
    },
    {
      key: 'form',
      icon: <MailOutlined />,
      title: '信息填写',
      status: [smtpStatus].filter((s): s is ConfigStatus => s !== null),
      panel: (
        <Alert
          type="info"
          showIcon
          message="邮件 SMTP 的全部连接参数已统一在「服务凭据 → 邮件 SMTP」维护"
          description="注册邮箱验证码、密码重置、客户档案通知与官网留言邮件转发共用这一套 SMTP。点右上角「配置凭据」进入凭据页填写发件服务器、发件邮箱与授权码，保存后可在那里直接「发送测试邮件」验证链路。本步无需再填字段。"
        />
      ),
    },
    {
      key: 'identity',
      icon: <IdcardOutlined />,
      title: '实名核验',
      status: identityStatus ? [identityStatus] : [],
      panel: <IdentityForm />,
    },
    {
      key: 'sms',
      icon: <MessageOutlined />,
      title: '手机号持有验证',
      status: smsStatus ? [smsStatus] : [],
      panel: <SmsForm />,
    },
  ];

  const cur = steps[current];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              <SafetyCertificateOutlined style={{ color: '#0d9488', marginRight: 8 }} />
              注册流程配置
            </div>
            <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
              新用户注册按以下 4 步完成。点击步骤切换对应配置，每步仅展示业务开关与模板，第三方 API 凭据在「服务凭据」页统一维护。
            </div>
          </div>
          <Space>
            <Button icon={<KeyOutlined />} onClick={goCredentials}>服务凭据</Button>
            <Button onClick={() => navigate('/settings')}>返回</Button>
          </Space>
        </div>
        <Steps
          current={current}
          onChange={setCurrent}
          style={{ marginTop: 20 }}
          items={steps.map((s, i) => ({ key: s.key, title: `${i + 1}. ${s.title}`, icon: s.icon }))}
        />
      </Card>

      <div style={{ maxWidth: 760, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Alert type="info" showIcon message={STEP_HINTS[current]} />
        <StepHeader status={cur.status} onCredentials={cur.key === 'terms' ? undefined : goCredentials} />
        {cur.panel}
      </div>

      <Alert
        type="warning"
        showIcon
        icon={<CheckCircleOutlined />}
        message="生产环境上线检查清单"
        description="① 短信服务商由 mock 切换为 aliyun（腾讯云短信后端未实现，已从可选项移除）；② 实名认证服务商由 test 切换为真实渠道，并关闭「模拟验证不通过」；③ SMTP 配置完成后用「发送测试邮件」验证通过；④「站点设置」补齐客服电话、公司地址与备案号——留空即四端整行不显示。"
      />
    </div>
  );
}
